import { AlertMessage } from '@/app/components'
import { Button, Text } from '@/components'
import TextFormField from '@/components/forms/text-form-field'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { SelectInput } from '@/app/components'
import SurveyObserver, {
  useSurveyResponder,
} from '@/app/components/SurveyObserver'
import { useProgramRequirements } from '@/app/hooks/useProgramRequirements'
import { useMultiProgramRouteContext } from '../..'
import { useAppSelector } from '@/hooks'
import {
  programsNewCollection,
  selectRequiredEnrollmentSurveyResponses,
} from '@/reducers/programsNew'

type SchemaType = yup.InferType<typeof schema>
const schema = yup
  .object({
    smart_charging: yup.bool().required(),
    charger_brand: yup.string().optional(),
    charger_model: yup.string().optional(),
  })
  .test(
    'brandAndModelRequired',
    'Brand and model must be filled out if you use smart charging',
    (value) =>
      value.smart_charging
        ? value.charger_brand?.length !== 0 && value.charger_model?.length !== 0
        : true,
  )
export default function MassCleanPeak() {
  const { utilityProgram } = useMultiProgramRouteContext()
  const { refetch } = programsNewCollection.useFetch()
  const surveys = useAppSelector(
    selectRequiredEnrollmentSurveyResponses(utilityProgram.id),
  )
  const survey = surveys.find((s) => s.survey.type === 'MassCleanPeak')
  const { routeToNextStep } = useProgramRequirements(utilityProgram)
  const {
    register,
    formState: { isValid },
    setValue,
    watch,
    handleSubmit,
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      smart_charging: survey?.response?.smart_charging as boolean,
      charger_brand: survey?.response?.charger_brand as string | undefined,
      charger_model: survey?.response?.charger_model as string | undefined,
    },
  })
  const { submitResponse } = useSurveyResponder('MassCleanPeak')
  const options = ['Yes', 'No']
  const onSelectChange = (index: number | null) => {
    setValue('smart_charging', index === 0 ? true : false, {
      shouldDirty: true,
      shouldValidate: true,
    })
  }
  const showTextInputFields = watch('smart_charging')
  const onSubmit = async (data: SchemaType) => {
    submitResponse(data)
    routeToNextStep()
    refetch()
  }
  return (
    <SurveyObserver observedSurveys={['MassCleanPeak']}>
      <div className="flex flex-col gap-4 h-full">
        <Text variant="h3">Do you charge your EV with a smart charger?</Text>
        <form
          className="flex flex-col gap-2 h-full"
          onSubmit={handleSubmit(onSubmit)}
        >
          <SelectInput
            onChange={onSelectChange}
            options={options}
            initialValue={
              survey?.response?.smart_charging === true
                ? 0
                : survey?.response?.smart_charging === false
                ? 1
                : null
            }
          />
          <div className={`${showTextInputFields ? 'contents' : 'hidden'}`}>
            <TextFormField
              name="charger_brand"
              label={'Charger Brand'}
              required={showTextInputFields}
              register={register}
            />
            <TextFormField
              name="charger_model"
              label={'Charger Model'}
              required={showTextInputFields}
              register={register}
            />
            <AlertMessage variant="info">
              We need to know if you have a smart (WiFi-connected) charger
              connected to a 240V outlet. Examples brands include (but are not
              limited to) ChargePoint, Tesla, Clipper Creek, EvoCharge, WallBox
              and Lectron.
            </AlertMessage>
          </div>
          <span className="flex-auto"></span>
          <Button
            id="submit-masscleanpeak-survey"
            disabled={!isValid}
            type="submit"
          >
            Next
          </Button>
        </form>
      </div>
    </SurveyObserver>
  )
}
